import React, { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash, Eye, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Attachments } from "@/components/Attachments";

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

const STATUSES = ["open", "in_progress", "fixed", "verified", "closed"];
const STATUS_LABEL = { open: "Open", in_progress: "In Progress", fixed: "Fixed", verified: "Verified", closed: "Closed" };
const STATUS_COLORS = {
  open: "border-red-300 bg-red-50/40",
  in_progress: "border-amber-300 bg-amber-50/40",
  fixed: "border-sky-300 bg-sky-50/40",
  verified: "border-emerald-300 bg-emerald-50/40",
  closed: "border-slate-300 bg-slate-50",
};
const PRIORITY_PILL = {
  P1: "bg-red-100 text-red-800",
  P2: "bg-amber-100 text-amber-800",
  P3: "bg-slate-100 text-slate-700",
};

export default function Snags({ projectId, embedded = false }) {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const empty = {
    project_id: projectId || "", title: "", description: "", location: "",
    priority: "P2", status: "open", assignee: "", due_date: "", notes: "",
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      api.get("/snags", { params: projectId ? { project_id: projectId } : {} }),
      api.get("/projects"),
    ]);
    setRows(a.data); setProjects(b.data);
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));

  const submit = async () => {
    try {
      await api.post("/snags", {
        ...form,
        project_id: projectId || form.project_id || projects[0]?.id,
        due_date: form.due_date || null,
      });
      toast.success("Snag logged");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const changeStatus = async (s, status) => {
    try {
      await api.put(`/snags/${s.id}`, { ...s, status });
      toast.success(`→ ${STATUS_LABEL[status]}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this snag?")) return;
    try { await api.delete(`/snags/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const grouped = STATUSES.reduce((acc, s) => {
    acc[s] = rows.filter((r) => r.status === s);
    return acc;
  }, {});

  const formSheet = (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button data-testid="create-snag-button" size={embedded ? "sm" : "default"} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">
          <Plus size={14} className="mr-1" /> Log Snag
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[520px] sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display">New Snag / Punch Item</SheetTitle></SheetHeader>
        <div className="space-y-3 mt-6">
          <Field label="Title"><Input data-testid="snag-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Cooling coil leak in AHU-3" /></Field>
          <Field label="Description"><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="3F East plant room" /></Field>
            <Field label="Assignee"><Input value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} placeholder="Ravi (Site engineer)" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Priority">
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="P1">P1 · Critical</SelectItem><SelectItem value="P2">P2 · Major</SelectItem><SelectItem value="P3">P3 · Minor</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Due date"><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></Field>
          </div>
          {!projectId && (
            <Field label="Project">
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <Button onClick={submit} data-testid="save-snag-button" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">Log Snag</Button>
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div data-testid="snags-page">
      {!embedded && (
        <PageHeader
          title="Snag List"
          subtitle="Punch-list items with priority, status, and photo evidence per site"
          module="Service · SV"
          actions={formSheet}
        />
      )}
      {embedded && (
        <div className="flex items-center justify-between mb-4">
          <div className="label-caps">Snags ({rows.length})</div>
          {formSheet}
        </div>
      )}

      <div className={embedded ? "" : "p-8"}>
        {rows.length === 0 ? (
          <EmptyState title="No snags yet" description="Log punch-list items during walkdowns. Each snag can carry photos, comments, and be tracked through open → verified." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="snags-kanban">
            {STATUSES.map((s) => (
              <div key={s} className="bg-white border border-slate-200 rounded-sm">
                <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-700">{STATUS_LABEL[s]}</div>
                  <div className="text-[11px] text-slate-500 font-mono-num">{grouped[s].length}</div>
                </div>
                <div className="p-2 space-y-2 min-h-[80px]">
                  {grouped[s].map((snag) => (
                    <div key={snag.id} className={`border rounded-sm p-2 cursor-pointer ${STATUS_COLORS[s]}`} onClick={() => setViewing(snag)} data-testid={`snag-card-${snag.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[9px] font-bold uppercase tracking-widest px-1 py-0.5 rounded-sm ${PRIORITY_PILL[snag.priority]}`}>{snag.priority}</span>
                        <span className="text-[9px] font-mono text-slate-500">{snag.snag_no}</span>
                      </div>
                      <div className="text-[13px] font-medium text-slate-900 line-clamp-2">{snag.title}</div>
                      <div className="text-[10.5px] text-slate-500 mt-1 flex items-center justify-between">
                        <span>{projMap[snag.project_id] || "—"}</span>
                        <span>{snag.assignee || "unassigned"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${PRIORITY_PILL[viewing.priority]}`}>{viewing.priority}</span>
                  {viewing.title}
                </DialogTitle>
                <div className="text-xs text-slate-500 mt-1">{viewing.snag_no} · {fmtDate(viewing.created_at)} · {projMap[viewing.project_id] || "—"}</div>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {viewing.description && <div><div className="label-caps mb-1">Description</div><div className="text-slate-700 whitespace-pre-wrap">{viewing.description}</div></div>}
                {viewing.location && <div><div className="label-caps mb-1">Location</div><div className="text-slate-700">{viewing.location}</div></div>}
                {viewing.assignee && <div><div className="label-caps mb-1">Assignee</div><div className="text-slate-700">{viewing.assignee}</div></div>}
                {viewing.due_date && <div><div className="label-caps mb-1">Due</div><div className="text-slate-700 font-mono-num">{fmtDate(viewing.due_date)}</div></div>}

                <div>
                  <div className="label-caps mb-2">Change status</div>
                  <div className="flex flex-wrap gap-1.5">
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        onClick={() => changeStatus(viewing, s)}
                        disabled={viewing.status === s}
                        className={`px-2.5 py-1 text-[11px] uppercase tracking-widest font-semibold rounded-sm border ${
                          viewing.status === s ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-slate-600 border-slate-300 hover:border-[#EA580C] hover:text-[#EA580C]"
                        }`}
                        data-testid={`change-status-${s}`}
                      >{STATUS_LABEL[s]}</button>
                    ))}
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-200">
                  <div className="label-caps mb-2 flex items-center gap-1"><Paperclip size={12} /> Photos & files</div>
                  <Attachments parentType="snag" parentId={viewing.id} category="snag_photo" />
                </div>

                <div className="flex justify-end pt-2">
                  <button onClick={() => { del(viewing.id); setViewing(null); }} className="text-xs text-red-600 hover:underline flex items-center gap-1">
                    <Trash size={12} /> Delete snag
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
